/**
 * Tenant App Component
 * 
 * Handles routing for tenant subdomains ({tenant}.eduna.com)
 * Requires authentication and tenant context
 */

import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';

import { checkAuthStatus } from '../store/slices/authSlice';
import ProtectedRoute from '../components/common/ProtectedRoute';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { PermissionProvider } from '../contexts/PermissionContext';
import { TenantProvider } from '../contexts/TenantContext';
import useDynamicPWA from '../hooks/useDynamicPWA';

// Lazy load components
const TenantLogin = React.lazy(() => import('../views/auth/TenantLogin'));
const TenantSelection = React.lazy(() => import('../views/auth/TenantSelection'));
const ForgotPassword = React.lazy(() => import('../views/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('../views/auth/ResetPassword'));
const Onboarding = React.lazy(() => import('../views/auth/Onboarding'));
const GoogleAuthSuccess = React.lazy(() => import('../views/auth/GoogleAuthSuccess'));
const DefaultLayout = React.lazy(() => import('../layout/DefaultLayout'));
const OnboardingLayout = React.lazy(() => import('../layout/OnboardingLayout'));

function TenantApp() {
  // Load dynamic PWA manifest for tenant branding
  useDynamicPWA();
  
  const dispatch = useDispatch();
  const { isAuthenticated, user, token } = useSelector((state) => state.auth);
  const [isRehydrated, setIsRehydrated] = React.useState(false);
  const [authChecked, setAuthChecked] = React.useState(false);

  useEffect(() => {
    // Wait for redux-persist to rehydrate
    const timer = setTimeout(() => {
      setIsRehydrated(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Check auth status after rehydration
    if (isRehydrated && !authChecked) {
      if (token) {
        dispatch(checkAuthStatus()).finally(() => {
          setAuthChecked(true);
        });
      } else {
        setAuthChecked(true);
      }
    }
  }, [isRehydrated, authChecked, token, dispatch]);

  // Show loading while rehydrating or checking auth
  if (!isRehydrated || !authChecked) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary>
      <TenantProvider>
        <PermissionProvider>
          <Helmet>
            <title>EDUNA Platform</title>
            <meta name="description" content="Educational and Professional Course Management System" />
          </Helmet>
          
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<TenantLogin />} />
              <Route path="/select-tenant" element={<TenantSelection />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
              
              {/* Onboarding route with dedicated layout */}
              <Route 
                path="/onboarding" 
                element={
                  <ProtectedRoute requireAuth={true}>
                    <OnboardingLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Onboarding />} />
              </Route>
              
              {/* Protected routes */}
              <Route 
                path="/*" 
                element={
                  <ProtectedRoute requireAuth={true} requireOnboarding={true}>
                    <DefaultLayout />
                  </ProtectedRoute>
                } 
              />
              
              {/* Root redirect */}
              <Route 
                path="/" 
                element={
                  <Navigate 
                    to={
                      isAuthenticated 
                        ? user?.requiresOnboarding 
                          ? "/onboarding" 
                          : "/dashboard"
                        : "/login"
                    } 
                    replace 
                  />
                } 
              />
            </Routes>
          </Suspense>
        </PermissionProvider>
      </TenantProvider>
    </ErrorBoundary>
  );
}

export default TenantApp;
